export type FunnelCoverage =
  /** Nothing was ever measured for these communities, or measurement starts after the period ends. */
  | { kind: 'unmeasured' }
  /**
   * Measurement starts inside the period, so the numbers cover only part of what was asked for.
   * uncoveredDays is whole days and can be 0 when the gap is within the first day.
   */
  | { kind: 'partial'; startedAt: string; uncoveredDays: number }
  | { kind: 'full' };

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Index of the local calendar day, independent of how long that day actually lasted.
 *
 * Subtracting local midnights and dividing by 24h undercounts across a daylight-saving spring-forward:
 * in America/New_York, midnight on 7 March to midnight on 9 March 2026 is 47 elapsed hours, so the
 * division yields one uncovered day where two calendar days went unmeasured. Brazil has had no DST since
 * 2019, but clientTz comes from the browser and a producer abroad does have it.
 *
 * Date.UTC of the LOCAL year/month/day gives a day index with no offset changes in it.
 */
const toDayIndex = (date: Date) =>
  Math.floor(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) / MS_PER_DAY,
  );

/**
 * How much of the selected period the funnel actually measured.
 *
 * Three cases, not two. An empty funnel is easy to explain and a fully covered one needs no comment. The
 * one that misleads is in between: the dashboard defaults to the last 30 days while measurement is days
 * old, so a producer lands on real numbers covering a fraction of the period, sitting next to sales cards
 * covering all of it. Without a label they compare "vendi 40 no mês" with "o funil diz 3 vendas" and
 * either distrust the page or believe the funnel.
 *
 * The comparison is by INSTANT, not by calendar day. An earlier version normalised both ends to the day
 * to avoid a supposed timezone flip, and that was wrong twice over: the flip does not happen - local
 * midnight is a LATER instant than the same morning in UTC, so a measurement stamped that morning is
 * correctly "before" it - and rounding to the day hid real intraday gaps. With the TODAY period selected
 * and measurement starting at 18:00, day-rounding reported full coverage for a period that was
 * three-quarters unmeasured.
 *
 * uncoveredDays is therefore whole days and may be 0: the gap is real but shorter than a day, and the
 * copy says "as primeiras horas" instead of naming a day count.
 */
export const resolveFunnelCoverage = (
  collectionStartedAt: string | null,
  startDate: Date,
  endDate: Date,
): FunnelCoverage => {
  if (!collectionStartedAt) return { kind: 'unmeasured' };

  const started = new Date(collectionStartedAt);
  if (!Number.isFinite(started.getTime())) return { kind: 'full' };

  const from = new Date(startDate);
  const to = new Date(endDate);

  // Measurement predates the period, so everything asked for was measured.
  if (started.getTime() <= from.getTime()) return { kind: 'full' };

  // Measurement begins after the period ends, so nothing in it was measured. Compared at day
  // granularity: an end stamped 23:59:59 and a measurement later the same day still means the day was
  // partly measured, not that the period was missed entirely.
  if (toDayIndex(started) > toDayIndex(to)) return { kind: 'unmeasured' };

  return {
    kind: 'partial',
    startedAt: collectionStartedAt,
    uncoveredDays: toDayIndex(started) - toDayIndex(from),
  };
};
